import React, { useMemo, useState } from 'react';
import { useGetMyChildrenQuery, useReplyToMessageMutation } from '../../store/api/localApi';
import { MessageType, type MessageDelivery } from '../../types';

const REASONS: Record<string, string> = {
  STUDENT_NOT_IN_SCOPE: "This message wasn't sent about that child.",
  REPLIES_NOT_ALLOWED: 'Replies are closed for this message.',
  MESSAGE_WITHDRAWN: 'This message has been withdrawn.',
};

const NONE_REPLIED: string[] = [];

interface Props {
  delivery: MessageDelivery;
  /** The children this send already has a conversation about. Offered no
   *  second time; the parent's other children in scope still are. */
  alreadyRepliedStudentIds?: string[];
  onSent: (threadId: string, studentId: string) => void;
}

/**
 * Reply to a staff message about one child. A PARENT_DM already names the
 * child; a location or class send narrows to the parent's children at its
 * location; an org-wide send carries no target location, so all of them are
 * candidates. A pick is needed only when more than one candidate remains.
 */
const MessageReplyBox: React.FC<Props> = ({ delivery, alreadyRepliedStudentIds = NONE_REPLIED, onSent }) => {
  const { data } = useGetMyChildrenQuery();
  const [reply, { isLoading }] = useReplyToMessageMutation();
  const [body, setBody] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Class membership isn't available client-side, so a group send narrows by
  // location only, and an org-wide send not at all; the server enforces the
  // class and org scope. A DM is matched by type, not by studentId: once a
  // group send has been answered, its row carries the answered child's id too.
  // Departed children stay in getMyChildren for read-only history, but the
  // server only accepts a reply about an ACTIVE or PENDING_START child, so they
  // are never candidates.
  const candidates = useMemo(() => {
    if (!delivery.repliesAllowed) return [];
    const children = (data?.children ?? []).filter((c) => c.status !== 'LEFT');
    let inScope = children;
    if (delivery.type === MessageType.PARENT_DM) {
      inScope = children.filter((c) => c.id === delivery.studentId);
    } else if (delivery.targetLocationId) {
      inScope = children.filter((c) => c.locationId === delivery.targetLocationId);
    }
    return inScope.filter((c) => !alreadyRepliedStudentIds.includes(c.id));
  }, [
    data,
    delivery.repliesAllowed,
    delivery.type,
    delivery.studentId,
    delivery.targetLocationId,
    alreadyRepliedStudentIds,
  ]);

  const [studentId, setStudentId] = useState<string>('');
  const chosen = studentId || (candidates.length === 1 ? candidates[0].id : '');

  if (candidates.length === 0) return null;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!chosen || !body.trim()) return;
    setError(null);
    try {
      const res = await reply({
        messageId: delivery.messageId,
        deliveryId: delivery.id,
        studentId: chosen,
        body: body.trim(),
      }).unwrap();
      onSent(res.threadId, chosen);
    } catch (err) {
      const reason = (err as { data?: { reason?: string } })?.data?.reason;
      setError((reason && REASONS[reason]) || 'Could not send your reply.');
    }
  };

  return (
    <form onSubmit={submit} data-testid="message-reply-box" className="mt-4 mb-6 space-y-3">
      {candidates.length > 1 && (
        <select
          data-testid="message-reply-child"
          aria-label="Which child is this about?"
          value={chosen}
          onChange={(e) => setStudentId(e.target.value)}
          className="block w-full rounded-md border-gray-300 text-sm"
        >
          <option value="">Which child is this about?</option>
          {candidates.map((c) => (
            <option key={c.id} value={c.id}>
              {c.firstName} {c.lastName}
            </option>
          ))}
        </select>
      )}
      <textarea
        data-testid="message-reply-body"
        aria-label="Reply"
        value={body}
        onChange={(e) => setBody(e.target.value)}
        maxLength={2000}
        rows={3}
        placeholder="Write a reply…"
        className="block w-full rounded-md border-gray-300 text-sm"
      />
      {error && (
        <p data-testid="message-reply-error" className="text-sm text-red-600">
          {error}
        </p>
      )}
      <button
        type="submit"
        data-testid="message-reply-submit"
        disabled={isLoading || !chosen || !body.trim()}
        className="inline-flex items-center rounded-md bg-primary-600 px-3.5 py-2 text-sm font-medium text-white disabled:opacity-50"
      >
        {isLoading ? 'Sending…' : 'Reply'}
      </button>
    </form>
  );
};

export default MessageReplyBox;
